/**
 * Comparaison de deux numéros de version d'application (« 1.0.10 » et « 1.0.9 »).
 *
 * POURQUOI UNE FONCTION PLUTÔT QU'UN OPÉRATEUR. `'1.0.10' < '1.0.9'` est VRAI : une comparaison de
 * chaînes procède caractère par caractère, et '1' précède '9'. Utilisée telle quelle pour décider
 * d'une mise à jour obligatoire, elle bloquerait en priorité les utilisateurs LES PLUS À JOUR tout
 * en laissant passer les retardataires — l'exact inverse de sa raison d'être, et sans symptôme
 * jusqu'à la dixième version corrective. On compare donc des NOMBRES, segment par segment.
 *
 * `null` SIGNIFIE « JE NE SAIS PAS », ET C'EST UN RÉSULTAT UTILE. Ce que l'appelant reçoit ici
 * décide de bloquer ou non l'application ; une valeur illisible ne doit donc pas être réparée au
 * jugé mais refusée. La règle qui en découle est la propriété de sûreté de tout le mécanisme :
 * TOUTE ENTRÉE DOUTEUSE VAUT `null`, JAMAIS `-1`. Comme le blocage se déclenche sur un résultat
 * strictement négatif, une colonne mal saisie en base ou une version illisible laissent
 * l'application ouverte au lieu de la condamner — le fail-open est une conséquence du typage, pas
 * une précaution à réécrire à chaque appel.
 *
 * Les deux entrées sont `unknown` à dessein : la version installée est déclarée `string | null` par
 * expo-application, et la version de référence sort d'une réponse JSON non validée.
 */

/** Suite de nombres séparés par des points, et rien d'autre : ni « v1.0 », ni « 1.0.1-beta ». */
const FORME_VERSION = /^\d+(\.\d+)*$/;

/**
 * Découpe une version en segments numériques. Renvoie `null` si la forme n'est pas reconnue —
 * y compris pour ce qui n'est pas une chaîne, cas ordinaire ici et non défensif.
 */
function segments(version: unknown): number[] | null {
  if (typeof version !== 'string') return null;
  const nettoyee = version.trim();
  if (!FORME_VERSION.test(nettoyee)) return null;
  return nettoyee.split('.').map(Number);
}

/**
 * -1 si `a` est plus ancienne que `b`, 1 si elle est plus récente, 0 si elles sont équivalentes,
 * `null` si l'une des deux n'est pas lisible.
 *
 * Les segments absents valent zéro : « 1.0 » et « 1.0.0 » désignent la même version. Sans cela, une
 * valeur saisie à la main en base (« 1.1 ») et une valeur issue du binaire (« 1.1.0 ») seraient
 * comparées sur des longueurs différentes.
 */
export function comparerVersions(a: unknown, b: unknown): -1 | 0 | 1 | null {
  const gauche = segments(a);
  const droite = segments(b);
  if (!gauche || !droite) return null;

  const longueur = Math.max(gauche.length, droite.length);
  for (let i = 0; i < longueur; i += 1) {
    const g = gauche[i] ?? 0;
    const d = droite[i] ?? 0;
    if (g !== d) return g > d ? 1 : -1;
  }
  return 0;
}
